import { useState } from 'react';
import { IMaskInput } from 'react-imask';
import { Field, FieldLabel, FormField, FormModal, FormRow, Select, TextArea } from '../../components';
import { formatPhone } from '../../utils/callLog';
import { useCreateVendor, useUpdateVendor } from '../../queries/vendors';
import { STATUS_OPTIONS, TRADES } from './constants';

const FIELDS = ['company', 'contact', 'trade', 'phone', 'email', 'coverage', 'rate', 'status', 'notes'];

const toForm = (v = {}) => ({
  ...Object.fromEntries(FIELDS.map((k) => [k, v[k] || ''])),
  phone: v.phone ? formatPhone(v.phone) : '',
  status: v.status || 'active',
  insuranceExpiresAt: v.insuranceExpiresAt ? v.insuranceExpiresAt.slice(0, 10) : ''
});

const toPayload = (form) => ({
  ...Object.fromEntries(FIELDS.map((k) => [k, form[k].trim()])),
  insuranceExpiresAt: form.insuranceExpiresAt || null
});

export default function VendorFormModal({ vendor, trades, onClose }) {
  const editing = Boolean(vendor?._id);
  const [form, setForm] = useState(() => toForm(vendor));
  const create = useCreateVendor();
  const update = useUpdateVendor();
  const valid = Boolean(form.company.trim());

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const save = () => {
    const payload = toPayload(form);
    if (editing) {
      update.mutate({ _id: vendor._id, ...payload });
      onClose();
    } else {
      create.mutate(payload, { onSuccess: onClose });
    }
  };

  const tradeOptions = [...new Set([...TRADES, ...(trades || []), vendor?.trade].filter(Boolean))];

  return (
    <FormModal
      title={editing ? `Edit ${vendor.company}` : 'Add vendor'}
      subtitle={editing ? 'Update this trade partner' : 'New approved trade partner'}
      okText={editing ? 'Save changes' : 'Add vendor'}
      valid={valid}
      loading={create.isPending}
      onSubmit={save}
      onClose={onClose}
    >
        <FormField>
          <FieldLabel htmlFor="vendor-company">Company name</FieldLabel>
          <Field
            id="vendor-company"
            required
            autoFocus
            placeholder="Business or crew name"
            value={form.company}
            onChange={set('company')}
          />
        </FormField>
        <FormRow>
          <FormField>
            <FieldLabel htmlFor="vendor-contact">Primary contact</FieldLabel>
            <Field id="vendor-contact" placeholder="Name or dispatch" value={form.contact} onChange={set('contact')} />
          </FormField>
          <FormField>
            <FieldLabel htmlFor="vendor-trade">Trade</FieldLabel>
            <Select id="vendor-trade" style={{ minWidth: 0 }} value={form.trade} onChange={set('trade')}>
              <option value="">Select a trade</option>
              {tradeOptions.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </Select>
          </FormField>
        </FormRow>
        <FormRow>
          <FormField>
            <FieldLabel htmlFor="vendor-phone">Phone</FieldLabel>
            <Field
              as={IMaskInput}
              id="vendor-phone"
              type="tel"
              mask="(000) 000-0000"
              placeholder="(555) 555-0100"
              value={form.phone}
              onAccept={(phone) => setForm((f) => ({ ...f, phone }))}
            />
          </FormField>
          <FormField>
            <FieldLabel htmlFor="vendor-email">Email</FieldLabel>
            <Field id="vendor-email" type="email" placeholder="dispatch@vendor.com" value={form.email} onChange={set('email')} />
          </FormField>
        </FormRow>
        <FormField>
          <FieldLabel htmlFor="vendor-coverage">Coverage</FieldLabel>
          <Field id="vendor-coverage" placeholder="Areas or properties served" value={form.coverage} onChange={set('coverage')} />
        </FormField>
        <FormRow>
          <FormField>
            <FieldLabel htmlFor="vendor-rate">Rate</FieldLabel>
            <Field id="vendor-rate" placeholder="$95/hr" value={form.rate} onChange={set('rate')} />
          </FormField>
          <FormField>
            <FieldLabel htmlFor="vendor-status">Status</FieldLabel>
            <Select id="vendor-status" style={{ minWidth: 0 }} value={form.status} onChange={set('status')}>
              {STATUS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </Select>
          </FormField>
        </FormRow>
        <FormField>
          <FieldLabel htmlFor="vendor-insurance">Insurance expires</FieldLabel>
          <Field id="vendor-insurance" type="date" value={form.insuranceExpiresAt} onChange={set('insuranceExpiresAt')} />
        </FormField>
        <FormField>
          <FieldLabel htmlFor="vendor-notes">Notes</FieldLabel>
          <TextArea
            id="vendor-notes"
            placeholder="After-hours terms, preferred dispatch, lockbox rules…"
            value={form.notes}
            onChange={set('notes')}
          />
        </FormField>
    </FormModal>
  );
}
